/**
 * composables/useAnalytics.ts
 *
 * Typed wrapper around Firebase Analytics so the app never calls logEvent()
 * (or raw event name strings) directly from components/services.
 */

import type { CheckoutEventsData } from '@paddle/paddle-js'
import { logEvent } from 'firebase/analytics'
import type { PaddlePriceAmount } from '@/composables/usePaddlePrices'
import { getAnalyticsInstance } from '@/services/telemetry'
import type { BillingFrequency, Tier } from '@/types/pricing'

// Identity provider id as reported by Firebase Auth (see GoogleAuthProvider.PROVIDER_ID)
export type AnalyticsAuthMethod = 'google.com'

/**
 * Log a `login` event for an existing user signing back in.
 */
export function logLogin (method: AnalyticsAuthMethod) {
    const analytics = getAnalyticsInstance()
    if (analytics) {
        logEvent(analytics, 'login', { method })
    }
}

/**
 * Log a `sign_up` event the first time a user is created.
 */
export function logSignUp (method: AnalyticsAuthMethod) {
    const analytics = getAnalyticsInstance()
    if (analytics) {
        logEvent(analytics, 'sign_up', { method })
    }
}

/**
 * Log a view of a plan's pricing using GA4's recommended ecommerce event.
 */
export function logViewItem (tier: Tier, frequency: BillingFrequency, amount?: PaddlePriceAmount) {
    const analytics = getAnalyticsInstance()
    if (analytics) {
        logEvent(analytics, 'view_item', {
            // GA4 requires `currency` whenever `value` is set
            ...(amount && { currency: amount.currency, value: amount.value }),
            items: [{
                item_id: tier.priceId[frequency],
                item_name: tier.name,
                item_variant: frequency,
                quantity: 1,
                ...(amount && { price: amount.value }),
            }],
        })
    }
}

/**
 * Log a completed Paddle checkout using GA4's recommended ecommerce event.
 * Amounts exclude tax, matching `begin_checkout`; Paddle reports them in major units.
 */
export function logPurchase (checkout: CheckoutEventsData) {
    const analytics = getAnalyticsInstance()
    if (analytics) {
        logEvent(analytics, 'purchase', {
            transaction_id: checkout.transaction_id,
            currency: checkout.currency_code,
            value: checkout.totals.subtotal - checkout.totals.discount,
            tax: checkout.totals.tax,
            items: checkout.items.map(item => ({
                item_id: item.price_id,
                item_name: item.product.name,
                ...(item.billing_cycle && { item_variant: item.billing_cycle.interval }),
                quantity: item.quantity,
                price: (item.totals.subtotal - item.totals.discount) / item.quantity,
            })),
        })
    }
}

/**
 * Log the start of a plan checkout using GA4's recommended ecommerce event.
 */
export function logBeginCheckout (tier: Tier, frequency: BillingFrequency, amount?: PaddlePriceAmount) {
    const analytics = getAnalyticsInstance()
    if (analytics) {
        logEvent(analytics, 'begin_checkout', {
            // GA4 requires `currency` whenever `value` is set
            ...(amount && { currency: amount.currency, value: amount.value }),
            items: [{
                item_id: tier.priceId[frequency],
                item_name: tier.name,
                item_variant: frequency,
                quantity: 1,
                ...(amount && { price: amount.value }),
            }],
        })
    }
}
